import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import { TherapistService } from "../../services/TherapistService";
import { AppointmentService } from "../../services/AppointmentService";
import { Util } from "../../Util";
import moment from "moment";
import { toast } from "react-toastify";
import ConfirmAppointmentModal from "../../components/ConfirmAppointmentModal";

interface ClientDashboardCalendarProps {
  primaryTherapist?: {
    firstname?: string;
    lastname?: string;
    _id?: string;
  };
  clientId: string; // Add clientId to props
}

const ClientDashboardCalendar: React.FC<ClientDashboardCalendarProps> = ({ primaryTherapist, clientId }) => {
  const therapistFirstName = primaryTherapist?.firstname || 'Saman';
  const therapistLastName = primaryTherapist?.lastname || 'Perera';

  // States để quản lý lịch
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [monthName, setMonthName] = useState("");

  // State cho time slots pagination
  const [timeSlotPage, setTimeSlotPage] = useState<number>(0);
  const TIME_SLOTS_PER_PAGE = 8;

  // State cho available dates
  const [allAvailableDates, setAllAvailableDates] = useState<{date: Date, day: number, month: number, year: number}[]>([]);
  const [currentDatePage, setCurrentDatePage] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const DATES_PER_PAGE = isMobile ? 5 : 7; // Hiển thị 5 ngày trên mobile, 7 ngày trên desktop

  // States cho available slots của therapist
  const [therapistId, setTherapistId] = useState<string>("");
  const [therapistAvailableHours, setTherapistAvailableHours] = useState<any[]>([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState<boolean>(false);
  const [therapistData, setTherapistData] = useState<any>(null);
  const [allBlockDatesListInTherapist, setAllBlockDatesListInTherapist] = useState<any[]>([]);
  const [allAppointmentListInTherapist, setAllAppointmentListInTherapist] = useState<any[]>([]);

  // State for appointment modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State và ref cho tính năng vuốt (swipe)
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [swiping, setSwiping] = useState(false); // Trạng thái đang vuốt
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null); // Hướng vuốt
  const dateScrollContainerRef = useRef<HTMLDivElement>(null);

  // Mảng tên tháng
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Mảng tên ngày trong tuần
  const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Cập nhật tên tháng khi currentMonth thay đổi
  useEffect(() => {
    setMonthName(monthNames[currentMonth]);
  }, [currentMonth]);

  // Theo dõi kích thước màn hình để cập nhật responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Thêm event listener
    window.addEventListener('resize', handleResize);

    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Lấy dữ liệu therapist khi component được mount
  useEffect(() => {
    if (primaryTherapist?._id) {
      setTherapistId(primaryTherapist._id);
    }
  }, [primaryTherapist]);

  // Gọi API lấy availability khi therapistId thay đổi
  useEffect(() => {
    if (therapistId) {
      getTherapistAvailability();
    }
  }, [therapistId]);

  // Gọi API lấy lịch cho tháng cụ thể khi tháng thay đổi
  useEffect(() => {
    if (therapistId && therapistData) {
      selectAnotherMonthAndRefreshAppointmentList(new Date(currentYear, currentMonth, 1));
    }
  }, [therapistId, currentMonth, currentYear, therapistData]);

  // Tự động chọn ngày đầu tiên khi danh sách ngày có sẵn được cập nhật
  useEffect(() => {
    if (allAvailableDates.length > 0) {
      const firstAvailableDate = allAvailableDates[0];

      // Cập nhật tháng và năm hiện tại nếu cần
      if (firstAvailableDate.month !== currentMonth || firstAvailableDate.year !== currentYear) {
        setCurrentMonth(firstAvailableDate.month);
        setCurrentYear(firstAvailableDate.year);

        // Cập nhật tên tháng
        const selectedMonthDate = new Date(firstAvailableDate.year, firstAvailableDate.month, 1);
        updateMonthName(selectedMonthDate);
      }

      // Chọn ngày đầu tiên
      handleDateSelect(firstAvailableDate.day);

      // console.log("Auto-selected first available date:", firstAvailableDate);
    }
  }, [allAvailableDates]);

  // Hàm chuyển đổi ngày trong tuần sang số
  const dayOfWeekAsNumber = (day: string): number => {
    const dayMap: Record<string, number> = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6
    };
    return dayMap[day];
  };

  // Hàm lấy thông tin lịch rảnh của therapist
  const getTherapistAvailability = async () => {
    if (!therapistId) return;

    try {
      setIsLoadingAvailability(true);
      const res = await TherapistService.getTherapistDetailsbyIdPublic(therapistId);

      if (res?.success && res.data) {
        setTherapistData(res.data);
        const availableDays: any[] = [];

        if (res.data.workingHours?.length) {
          res.data.workingHours.forEach((obj: any) => {
            const dayAsNumber = dayOfWeekAsNumber(obj.day);

            if (Util.convertUTCDateToLocalDate(obj.endTime) > Util.convertUTCDateToLocalDate(obj.startTime)) {
              availableDays.push({
                startTime: Util.convertUTCDateToLocalDate(obj.startTime),
                endTime: Util.convertUTCDateToLocalDate(obj.endTime),
                daysOfWeek: [dayAsNumber],
              });
            } else {
              availableDays.push({
                startTime: Util.convertUTCDateToLocalDate(obj.startTime),
                endTime: "24:00 AM",
                daysOfWeek: [dayAsNumber - 1],
              });

              availableDays.push({
                startTime: "00:00 AM",
                endTime: Util.convertUTCDateToLocalDate(obj.endTime),
                daysOfWeek: [dayAsNumber],
              });
            }
          });
        } else {
          availableDays.push({
            startTime: "00:00 AM",
            endTime: "00:00 PM",
            daysOfWeek: [10], // Một ngày không tồn tại
          });
        }

        setTherapistAvailableHours(availableDays);

        // Lưu trữ toàn bộ blocked dates
        const blockedDates = res.data.blockedDates || [];
        setAllBlockDatesListInTherapist(blockedDates);

        // Lấy danh sách cuộc hẹn đã đặt cho 3 tháng liên tiếp
        const currentMonthDate = new Date(currentYear, currentMonth, 1);
        const appointments = await AppointmentService.getAllAppointmentByTherapistIdPublicForMonth(therapistId, currentMonthDate);

        // Tính toán tháng tiếp theo
        const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
        const nextMonthAppointments = await AppointmentService.getAllAppointmentByTherapistIdPublicForMonth(therapistId, nextMonthDate);

        // Tính toán tháng sau nữa
        const nextTwoMonthDate = new Date(currentYear, currentMonth + 2, 1);
        const nextTwoMonthAppointments = await AppointmentService.getAllAppointmentByTherapistIdPublicForMonth(therapistId, nextTwoMonthDate);

        // Kết hợp dữ liệu của cả ba tháng
        if (appointments?.success && nextMonthAppointments?.success && nextTwoMonthAppointments?.success) {
          // Chuyển dates thành Date objects
          const currentMonthUpdatedAppointments = appointments.data.map((appointment: any) => ({
            ...appointment,
            start: new Date(appointment.start),
            end: new Date(appointment.end)
          }));

          const nextMonthUpdatedAppointments = nextMonthAppointments.data.map((appointment: any) => ({
            ...appointment,
            start: new Date(appointment.start),
            end: new Date(appointment.end)
          }));

          const nextTwoMonthUpdatedAppointments = nextTwoMonthAppointments.data.map((appointment: any) => ({
            ...appointment,
            start: new Date(appointment.start),
            end: new Date(appointment.end)
          }));

          // Kết hợp tất cả các cuộc hẹn
          const allAppointments = [
            ...currentMonthUpdatedAppointments,
            ...nextMonthUpdatedAppointments,
            ...nextTwoMonthUpdatedAppointments
          ];

          // Lưu toàn bộ danh sách cuộc hẹn
          setAllAppointmentListInTherapist(allAppointments);

          // Cập nhật tên tháng hiện tại
          updateMonthName(currentMonthDate);

          // Tính toán tất cả các ngày có sẵn cho 3 tháng
          calculateAllAvailableDates(availableDays, blockedDates, allAppointments);
        }
      }
    } catch (error) {
      console.error('Error getting therapist data:', error);
      toast.error("Không thể tải thông tin lịch của therapist");
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  // Hàm cập nhật tên tháng
  const updateMonthName = (date: Date) => {
    // Chỉ cập nhật tên tháng
    setMonthName(monthNames[date.getMonth()]);
    //console.log("Month updated to:", monthNames[date.getMonth()]);
  };

  // Hàm tính toán tất cả các ngày có sẵn cho 3 tháng
  const calculateAllAvailableDates = (
    availableHours: any[],
    blockedDates: any[],
    allAppointments: any[]
  ) => {
    //console.log("Calculating all available dates...");
    //console.log("Available hours:", availableHours);
    //console.log("Blocked dates:", blockedDates);
    //console.log("All appointments:", allAppointments);

    const result: {date: Date, day: number, month: number, year: number}[] = [];
    const today = new Date();
    const tomorrowDate = moment(today).add(1, "day").startOf("day").toDate();

    // Tính toán cho 3 tháng
    for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
      const year = currentMonth + monthOffset > 11
        ? currentYear + 1
        : currentYear;
      const month = (currentMonth + monthOffset) % 12;

      // Lấy số ngày trong tháng
      const daysInMonth = getDaysInMonth(year, month);

      // Lọc blocked dates cho tháng này
      const filteredBlockedDates = blockedDates.filter((item: any) => {
        const startMonth = moment(item.start).month();
        const startYear = moment(item.start).year();
        const endMonth = moment(item.end).month();
        const endYear = moment(item.end).year();

        return (startMonth === month && startYear === year) ||
               (endMonth === month && endYear === year);
      });

      // Lọc appointments cho tháng này
      const filteredAppointments = allAppointments.filter((item: any) => {
        const startMonth = moment(item.start).month();
        const startYear = moment(item.start).year();
        const endMonth = moment(item.end).month();
        const endYear = moment(item.end).year();

        return (startMonth === month && startYear === year) ||
               (endMonth === month && endYear === year);
      });

      // Kiểm tra từng ngày trong tháng
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);

        // Chỉ kiểm tra các ngày trong tương lai
        if (moment(date).isBefore(tomorrowDate)) {
          continue;
        }

        // Kiểm tra các ngày không quá 60 ngày từ hiện tại để tránh hiển thị quá xa
        if (moment(date).isAfter(moment().add(60, 'days'))) {
          continue;
        }

        // Kiểm tra có time slots available thật sự không
        const dayOfWeek = date.getDay();

        // Tạo thử các time slots và kiểm tra sau khi lọc còn bao nhiêu
        const availableTimeSlots: string[][] = [];

        availableHours
          .filter((obj) => obj.daysOfWeek[0] === dayOfWeek)
          .forEach((obj) => {
            const availableHourSingle: string[] = [];

            const startHourMatch = obj.startTime.match(/(\d+):/);
            const endHourMatch = obj.endTime.match(/(\d+):/);

            if (startHourMatch && endHourMatch) {
              const startHour = parseInt(startHourMatch[1]);
              const endHour = parseInt(endHourMatch[1]);

              for (let hour = startHour; hour <= endHour; hour++) {
                if (hour === startHour) {
                  const mST = moment(obj.startTime, "HH:mm").minute();
                  if (mST === 0) {
                    availableHourSingle.push(moment({ hour }).format("h:mm A"));
                  }
                } else {
                  availableHourSingle.push(moment({ hour }).format("h:mm A"));
                }

                if (hour !== endHour) {
                  availableHourSingle.push(
                    moment({
                      hour,
                      minute: 30,
                    }).format("h:mm A")
                  );
                } else {
                  const mET = moment(obj.endTime, "HH:mm").minute();
                  if (mET === 30) {
                    availableHourSingle.push(
                      moment({
                        hour,
                        minute: 30,
                      }).format("h:mm A")
                    );
                  }
                }
              }
            }

            availableTimeSlots.push(availableHourSingle);
          });

        // Tạo tất cả time slots để kiểm tra có slots nào thật sự available không
        let allAvailableSlots: string[] = [];

        availableTimeSlots.forEach((slotArray: string[]) => {
          slotArray.forEach((slot: string) => {
            const slotTime = moment(slot, "h:mm A");
            const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);
            const endTime = moment(slotDate).add(60, "minutes");

            if (
              slotArray.includes(moment(slotDate).format("h:mm A")) &&
              slotArray.includes(moment(endTime).format("h:mm A"))
            ) {
              allAvailableSlots.push(slot);
            }
          });
        });

        // Loại bỏ các slot trùng lặp
        allAvailableSlots = [...new Set(allAvailableSlots)];

        // Lọc các slot bị block và đã có appointment
        allAvailableSlots = allAvailableSlots.filter(slot => {
          const slotTime = moment(slot, "h:mm A");
          const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);

          // Kiểm tra xem slot có nằm trong blocked dates không
          const isInBlockedRange = filteredBlockedDates.some((range: any) => {
            return Util.isDateInBlockedRange(moment(range.start), moment(range.end), slotDate);
          });

          if (isInBlockedRange) {
            return false;
          }

          // Kiểm tra xem slot đã có hẹn chưa
          const appointmentAlreadyExists = filteredAppointments.some((appointment: any) => {
            return Util.isDateInBlockedRange(moment(appointment.start), moment(appointment.end), slotDate);
          });

          return !isInBlockedRange && !appointmentAlreadyExists;
        });

        // Chỉ có ngày có ít nhất 1 slot available mới được coi là ngày available
        if (allAvailableSlots.length > 0) {
          result.push({
            date: new Date(year, month, day),
            day,
            month,
            year
          });
        }
      }
    }

    // Cập nhật state với tất cả các ngày có sẵn
    //console.log("All available dates calculated:", result);
    //console.log("Total available dates:", result.length);

    // Sắp xếp các ngày theo thứ tự tăng dần
    result.sort((a, b) => a.date.getTime() - b.date.getTime());

    setAllAvailableDates(result);
    // Reset về trang đầu tiên
    setCurrentDatePage(0);
  };



  // Hàm cập nhật dữ liệu khi chọn tháng khác
  const selectAnotherMonthAndRefreshAppointmentList = (selectedMonthIn: Date) => {
    try {
      if (therapistData && therapistId) {
        setIsLoadingAvailability(true);
        updateMonthName(selectedMonthIn);
        setIsLoadingAvailability(false);
      }
    } catch (error) {
      setIsLoadingAvailability(false);
      console.error("Error refreshing appointment list:", error);
    }
  };

  // Hàm helper để lấy số ngày trong tháng
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };



  // Hàm lấy ngày được chọn dưới dạng Date object
  const getSelectedDateObject = (): Date | null => {
    if (selectedDate === null) return null;

    // Tìm ngày được chọn trong danh sách allAvailableDates
    const selectedDateInfo = allAvailableDates.find(dateInfo =>
      dateInfo.day === selectedDate &&
      dateInfo.month === currentMonth &&
      dateInfo.year === currentYear
    );

    // Nếu tìm thấy, trả về date object từ allAvailableDates
    if (selectedDateInfo) {
      return selectedDateInfo.date;
    }

    // Nếu không tìm thấy, trả về date object được tạo từ currentYear, currentMonth, selectedDate
    return new Date(currentYear, currentMonth, selectedDate);
  };

  // Lấy available times cho ngày đã chọn
  const getAvailableTimesForSelectedDate = (): string[] => {
    //console.log("Getting available times for selected date...");
    //console.log("Selected date:", selectedDate);
    //console.log("Current month:", currentMonth);
    //console.log("Current year:", currentYear);

    if (!selectedDate) return [];

    // Tìm ngày được chọn trong danh sách allAvailableDates
    const selectedDateInfo = allAvailableDates.find(dateInfo =>
      dateInfo.day === selectedDate &&
      dateInfo.month === currentMonth &&
      dateInfo.year === currentYear
    );

    //console.log("Selected date info:", selectedDateInfo);

    // Nếu ngày không có trong danh sách ngày có sẵn, trả về mảng rỗng
    if (!selectedDateInfo) return [];

    const date = selectedDateInfo.date;
    const dayOfWeek = date.getDay();

    // Lấy các khung giờ available
    const availableHours: string[][] = [];

    therapistAvailableHours
      .filter((obj) => obj.daysOfWeek[0] === dayOfWeek)
      .forEach((obj) => {
        const availableHourSingle: string[] = [];

        // Extract hours from startTime and endTime
        const startHourMatch = obj.startTime.match(/(\d+):/);
        const endHourMatch = obj.endTime.match(/(\d+):/);

        if (startHourMatch && endHourMatch) {
          const startHour = parseInt(startHourMatch[1]);
          const endHour = parseInt(endHourMatch[1]);

          for (let hour = startHour; hour <= endHour; hour++) {
            if (hour === startHour) {
              const mST = moment(obj.startTime, "HH:mm").minute();
              if (mST === 0) {
                availableHourSingle.push(moment({ hour }).format("h:mm A"));
              }
            } else {
              availableHourSingle.push(moment({ hour }).format("h:mm A"));
            }

            if (hour !== endHour) {
              availableHourSingle.push(
                moment({
                  hour,
                  minute: 30,
                }).format("h:mm A")
              );
            } else {
              const mET = moment(obj.endTime, "HH:mm").minute();
              if (mET === 30) {
                availableHourSingle.push(
                  moment({
                    hour,
                    minute: 30,
                  }).format("h:mm A")
                );
              }
            }
          }
        }

        availableHours.push(availableHourSingle);
      });

    let allAvailableSlots: string[] = [];

    // Tạo các time slots đầy đủ
    availableHours.forEach((slotArray: string[]) => {
      slotArray.forEach((slot: string) => {
        const slotTime = moment(slot, "h:mm A");
        const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);
        const endTime = moment(slotDate).add(60, "minutes");

        // Kiểm tra xem cả slot bắt đầu và kết thúc có nằm trong working hours không
        if (
          moment(date).isAfter(moment().startOf('day')) && // Chỉ lấy các ngày từ ngày mai
          slotArray.includes(moment(slotDate).format("h:mm A")) &&
          slotArray.includes(moment(endTime).format("h:mm A"))
        ) {
          allAvailableSlots.push(slot);
        }
      });
    });

    // Loại bỏ các slot trùng lặp
    allAvailableSlots = [...new Set(allAvailableSlots)];

    // Lọc các slot nằm trong blocked dates
    allAvailableSlots = allAvailableSlots.filter(slot => {
      const slotTime = moment(slot, "h:mm A");
      const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);

      // Lấy tháng và năm của ngày được chọn
      const monthForCheck = date.getMonth();
      const yearForCheck = date.getFullYear();

      // Lọc blocked dates cho tháng của ngày được chọn
      const filteredBlockedDates = allBlockDatesListInTherapist.filter((item: any) => {
        const startMonth = moment(item.start).month();
        const startYear = moment(item.start).year();
        const endMonth = moment(item.end).month();
        const endYear = moment(item.end).year();

        return (startMonth === monthForCheck && startYear === yearForCheck) ||
               (endMonth === monthForCheck && endYear === yearForCheck);
      });

      // Lọc appointments cho tháng của ngày được chọn
      const filteredAppointments = allAppointmentListInTherapist.filter((item: any) => {
        const startMonth = moment(item.start).month();
        const startYear = moment(item.start).year();
        const endMonth = moment(item.end).month();
        const endYear = moment(item.end).year();

        return (startMonth === monthForCheck && startYear === yearForCheck) ||
               (endMonth === monthForCheck && endYear === yearForCheck);
      });

      // Kiểm tra xem slot có nằm trong blocked dates không
      const isInBlockedRange = filteredBlockedDates.some((range: any) => {
        return Util.isDateInBlockedRange(moment(range.start), moment(range.end), slotDate);
      });

      if (isInBlockedRange) {
        return false;
      }

      // Kiểm tra xem slot đã có hẹn chưa
      const appointmentAlreadyExists = filteredAppointments.some((appointment: any) => {
        return Util.isDateInBlockedRange(moment(appointment.start), moment(appointment.end), slotDate);
      });

      return !isInBlockedRange && !appointmentAlreadyExists;
    });

    // Sắp xếp các slots theo thời gian
    allAvailableSlots.sort((a, b) => {
      return moment(a, "h:mm A").valueOf() - moment(b, "h:mm A").valueOf();
    });

    //console.log("Available time slots:", allAvailableSlots);
    //console.log("Total available time slots:", allAvailableSlots.length);

    return allAvailableSlots;
  };

  // Xử lý khi chọn ngày
  const handleDateSelect = (day: number) => {
    setSelectedDate(day);
    setSelectedTime(null); // Reset selected time when date changes
    setTimeSlotPage(0); // Reset về trang đầu tiên khi chọn ngày mới
  };

  // Xử lý khi chọn giờ
  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setIsModalOpen(true);
  };

  // Xử lý khi chuyển trang time slots
  const handleNextTimeSlotPage = (availableTimes: string[]) => {
    const totalPages = Math.ceil(availableTimes.length / TIME_SLOTS_PER_PAGE);
    if (timeSlotPage < totalPages - 1) {
      setTimeSlotPage(prev => prev + 1);
    } else {
      // Quay lại trang đầu tiên khi đã hiển thị tất cả
      setTimeSlotPage(0);
    }
  };

  // Xử lý khi quay lại trang trước đó
  const handlePrevTimeSlotPage = (availableTimes: string[]) => {
    const totalPages = Math.ceil(availableTimes.length / TIME_SLOTS_PER_PAGE);
    if (timeSlotPage > 0) {
      setTimeSlotPage(prev => prev - 1);
    } else {
      // Quay lại trang cuối cùng khi đang ở trang đầu tiên
      setTimeSlotPage(totalPages - 1);
    }
  };



  // Hàm chuyển trang ngày
  const changeDatePage = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentDatePage > 0) {
      setCurrentDatePage(prev => prev - 1);
    } else if (direction === 'next' && (currentDatePage + 1) * DATES_PER_PAGE < allAvailableDates.length) {
      setCurrentDatePage(prev => prev + 1);
    }
  };

  // Các hàm xử lý sự kiện vuốt (swipe)
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null); // Reset touchEnd khi bắt đầu vuốt mới
    setSwiping(true); // Bắt đầu trạng thái vuốt
    setSwipeDirection(null); // Reset hướng vuốt
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const currentX = e.targetTouches[0].clientX;
    setTouchEnd(currentX);

    // Xác định hướng vuốt trong khi đang vuốt
    const distance = touchStart - currentX;
    if (distance > 20) {
      setSwipeDirection('left');
    } else if (distance < -20) {
      setSwipeDirection('right');
    } else {
      setSwipeDirection(null);
    }

    // Nếu đang vuốt, ngăn chặn cuộn trang
    if (Math.abs(distance) > 10) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50; // Vuốt sang trái (hiển thị ngày tiếp theo)
    const isRightSwipe = distance < -50; // Vuốt sang phải (hiển thị ngày trước đó)

    if (isLeftSwipe && (currentDatePage + 1) * DATES_PER_PAGE < allAvailableDates.length) {
      changeDatePage('next');
    } else if (isRightSwipe && currentDatePage > 0) {
      changeDatePage('prev');
    }

    // Reset các giá trị sau khi xử lý xong
    setTouchStart(null);
    setTouchEnd(null);
    setSwiping(false);
    setSwipeDirection(null);
  };

  // Tạo hiển thị các ngày có lịch rảnh
  const renderAvailableDates = () => {
    //console.log("Rendering available dates...");
    //console.log("All available dates:", allAvailableDates);
    //console.log("Current date page:", currentDatePage);

    if (isLoadingAvailability) {
      return (
        <div className="text-center py-3">
          <div>Loading available dates...</div>
        </div>
      );
    }

    // Nếu không có ngày nào available
    if (!allAvailableDates.length) {
      return (
        <div className="text-center py-3">
          <div style={{ color: '#666', fontSize: '14px' }}>No available dates found</div>
        </div>
      );
    }

    // Lấy các ngày cho trang hiện tại
    const startIndex = currentDatePage * DATES_PER_PAGE;
    const endIndex = Math.min(startIndex + DATES_PER_PAGE, allAvailableDates.length);
    const displayableDates = allAvailableDates.slice(startIndex, endIndex);

    //console.log("Displayable dates:", displayableDates);

    // Tính toán hiệu ứng chuyển động khi vuốt
    let swipeTransform = '';
    if (swiping && touchStart && touchEnd) {
      const distance = touchStart - touchEnd;
      // Giới hạn khoảng cách vuốt để tránh vuốt quá xa
      const maxDistance = 50;
      const limitedDistance = Math.max(Math.min(distance, maxDistance), -maxDistance);

      // Chỉ áp dụng hiệu ứng nếu có thể vuốt theo hướng đó
      if ((swipeDirection === 'left' && (currentDatePage + 1) * DATES_PER_PAGE < allAvailableDates.length) ||
          (swipeDirection === 'right' && currentDatePage > 0)) {
        swipeTransform = `translateX(${-limitedDistance}px)`;
      }
    }

    return (
      <div
        style={{
          display: 'flex',
          gap: isMobile ? '2px' : '10px',
          padding: isMobile ? '4px 0' : '8px 0',
          width: '100%',
          justifyContent: 'center',
          transition: 'transform 0.3s ease-out', // Hiệu ứng chuyển động mượt mà
          transform: swipeTransform, // Áp dụng hiệu ứng khi vuốt
          cursor: swiping ? (swipeDirection === 'left' ? 'e-resize' : (swipeDirection === 'right' ? 'w-resize' : 'grab')) : 'grab' // Thay đổi con trỏ khi vuốt
        }}
      >
        {displayableDates.map((dateInfo, index) => {
          const { date, day, month, year } = dateInfo;
          const dayOfWeek = date.getDay();

          // Kiểm tra xem ngày này có được chọn không
          // Cần kiểm tra cả ngày, tháng và năm vì có thể có ngày trùng nhau ở các tháng khác nhau
          const isSelected = selectedDate === day &&
                            currentMonth === month &&
                            currentYear === year;

          return (
            <div key={`day-${index}`} style={{
              flexShrink: 0,
              height: isMobile ? '70px' : '80px',
              width: isMobile ? '45px' : '64px'
            }}>
              <div
                onClick={() => {
                  //console.log("Selected date:", dateInfo);

                  // Cập nhật tháng và năm hiện tại để hiển thị đúng time slots
                  if (month !== currentMonth || year !== currentYear) {
                    //console.log("Updating month and year:", month, year);

                    // Cập nhật tháng và năm hiện tại
                    setCurrentMonth(month);
                    setCurrentYear(year);

                    // Cập nhật tên tháng
                    const selectedMonthDate = new Date(year, month, 1);
                    updateMonthName(selectedMonthDate);
                  }

                  // Chọn ngày
                  handleDateSelect(day);
                }}
                style={{
                  backgroundColor: isSelected ? '#FF8F2D' : '#FFF7EE',
                  borderRadius: isMobile ? '10px' : '12px',
                  padding: isMobile ? '4px 2px' : '10px',
                  height: '100%',
                  width: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: isSelected ? '1px solid #FF8F2D' : '1px solid #FFE0C0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: isSelected ? 'white' : 'inherit'
                }}
              >
                <div style={{
                  fontSize: isMobile ? '9px' : '12px',
                  color: isSelected ? 'rgba(255,255,255,0.9)' : '#666'
                }}>
                  {monthNames[month].substring(0, 3)}
                </div>
                <div style={{
                  fontSize: isMobile ? '16px' : '22px',
                  fontWeight: 'bold',
                  margin: isMobile ? '1px 0' : '3px 0',
                  color: isSelected ? 'white' : '#333'
                }}>
                  {day}
                </div>
                <div style={{
                  fontSize: isMobile ? '9px' : '12px',
                  color: isSelected ? 'rgba(255,255,255,0.9)' : '#666'
                }}>
                  {weekdayNames[dayOfWeek]}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render các khung giờ rảnh cho ngày được chọn với phân trang
  const renderAvailableTimes = () => {
    // Nếu chưa chọn ngày, không hiển thị gì
    if (!selectedDate) return null;

    const availableTimes = getAvailableTimesForSelectedDate();

    // Nếu không có khung giờ nào rảnh
    if (!availableTimes.length) {
      return (
        <div className="text-center py-3">
          <div style={{ color: '#666', fontSize: '14px' }}>No available times for this date</div>
        </div>
      );
    }

    const totalPages = Math.ceil(availableTimes.length / TIME_SLOTS_PER_PAGE);

    // Tính toán các time slots hiển thị trong trang hiện tại
    let displayableTimes: string[] = [];

    if (availableTimes.length <= TIME_SLOTS_PER_PAGE) {
      // Nếu tổng số timeslots ít hơn hoặc bằng số time slots hiển thị mỗi trang,
      // hiển thị tất cả
      displayableTimes = availableTimes;
    } else {
      // Lấy các time slots cho trang hiện tại
      const startIndex = timeSlotPage * TIME_SLOTS_PER_PAGE;
      let endIndex = startIndex + TIME_SLOTS_PER_PAGE;

      // Nếu là trang cuối cùng, có thể không đủ 8 slots
      if (endIndex > availableTimes.length) {
        endIndex = availableTimes.length;
      }

      displayableTimes = availableTimes.slice(startIndex, endIndex);
    }

    const selectedDateObj = getSelectedDateObject() || new Date(currentYear, currentMonth, selectedDate);
    const dayName = weekdayNames[selectedDateObj.getDay()];

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <p style={{ fontSize: '14px', fontWeight: 500, color: '#333', margin: 0 }}>
            {dayName}, {monthName} {selectedDate}
          </p>
          <span style={{ fontSize: '12px', padding: '3px 8px', backgroundColor: '#FFF7EE', color: '#FF8F2D', borderRadius: '12px', fontWeight: 500 }}>
            {moment(selectedDateObj).isSame(moment(), 'day') ? 'Today' : ''}
          </span>
        </div>

        <div style={{ minHeight: '168px' }}> {/* Giữ chiều cao cố định khi thay đổi các trang */}
          <Row>
            {displayableTimes.map((time, index) => (
              <Col xs="6" key={`time-${index}`} className="mb-3">
                <div
                  onClick={() => handleTimeSelect(time)}
                  style={{
                    backgroundColor: selectedTime === time ? '#FFF8EF' : '#FFF7EE',
                    borderRadius: '12px',
                    border: selectedTime === time ? '1px solid #F47B20' : '1px solid #FFE0C0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '14px',
                    cursor: 'pointer',
                    color: selectedTime === time ? '#F47B20' : '#333',
                    transition: 'all 0.2s ease',
                    height: '56px',
                    boxShadow: selectedTime === time ? '0px 2px 5px rgba(244, 123, 32, 0.2)' : 'none'
                  }}
                >
                  <i className="bx bx-time-five" style={{ color: '#FF8F2D', marginRight: '8px', fontSize: '16px' }}></i>
                  <span style={{ fontWeight: selectedTime === time ? '600' : '500' }}>{time}</span>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Thanh điều hướng phân trang */}
        {availableTimes.length > TIME_SLOTS_PER_PAGE && (
          <div className="text-center mt-3" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <Button
              onClick={() => handlePrevTimeSlotPage(availableTimes)}
              outline
              size="sm"
              disabled={timeSlotPage === 0}
              style={{
                borderColor: timeSlotPage === 0 ? '#e0e0e0' : '#F47B20',
                color: timeSlotPage === 0 ? '#cccccc' : '#F47B20',
                backgroundColor: 'white',
                fontSize: '14px',
                borderRadius: '50%',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: timeSlotPage === 0 ? 'not-allowed' : 'pointer',
                opacity: timeSlotPage === 0 ? 0.7 : 1
              }}
            >
              <i className="bx bx-chevron-left" style={{ fontSize: '16px' }}></i>
            </Button>

            <div style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#666',
              backgroundColor: '#FFF7EE',
              padding: '4px 12px',
              borderRadius: '12px'
            }}>
              {timeSlotPage + 1} of {totalPages}
            </div>

            <Button
              onClick={() => handleNextTimeSlotPage(availableTimes)}
              outline
              size="sm"
              disabled={timeSlotPage === totalPages - 1}
              style={{
                borderColor: timeSlotPage === totalPages - 1 ? '#e0e0e0' : '#F47B20',
                color: timeSlotPage === totalPages - 1 ? '#cccccc' : '#F47B20',
                backgroundColor: 'white',
                fontSize: '14px',
                borderRadius: '50%',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: timeSlotPage === totalPages - 1 ? 'not-allowed' : 'pointer',
                opacity: timeSlotPage === totalPages - 1 ? 0.7 : 1
              }}
            >
              <i className="bx bx-chevron-right" style={{ fontSize: '16px' }}></i>
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <Card style={{ background: '#fff', borderRadius: '12px', border: 'none', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>
      <CardBody style={{ padding: 0 }}>
        {/* Calendar Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0f0f0', backgroundColor: '#FFF7EE' }}>
          <i className="bx bx-calendar" style={{ color: '#FF8F2D', fontSize: '20px', marginRight: '10px' }}></i>
          <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#333' }}>Schedule with {therapistFirstName} {therapistLastName}</h5>
        </div>

        {/* Available Dates Section */}
        <div style={{ padding: isMobile ? '16px 10px' : '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <p style={{ fontSize: '14px', marginBottom: '12px', color: '#333', fontWeight: 500 }}>Available Dates:</p>

          {/* Date Navigation Container */}
          <div style={{ position: 'relative', padding: isMobile ? '0 30px' : '0 40px' }}>
            {/* Left Arrow */}
            <button
              onClick={() => changeDatePage('prev')}
              disabled={currentDatePage === 0}
              style={{
                position: 'absolute',
                left: isMobile ? '-10px' : '4px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '50%',
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                cursor: currentDatePage === 0 ? 'not-allowed' : 'pointer',
                opacity: currentDatePage === 0 ? 0.5 : 1
              }}
            >
              <i className="bx bx-chevron-left" style={{ color: '#666', fontSize: isMobile ? '16px' : '18px' }}></i>
            </button>

            {/* Date Scrolling Container */}
            <div
              ref={dateScrollContainerRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                width: '100%',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                touchAction: 'pan-y', // Cho phép vuốt dọc nhưng xử lý vuốt ngang
                WebkitOverflowScrolling: 'touch', // Cải thiện hiệu suất cuộn trên iOS
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
            >
              {/* Hiển thị chỉ báo khi vuốt đến giới hạn */}
              {swiping && swipeDirection === 'left' && (currentDatePage + 1) * DATES_PER_PAGE >= allAvailableDates.length && (
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  background: 'rgba(255, 143, 45, 0.5)',
                  borderRadius: '2px',
                  zIndex: 5
                }} />
              )}
              {swiping && swipeDirection === 'right' && currentDatePage === 0 && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  background: 'rgba(255, 143, 45, 0.5)',
                  borderRadius: '2px',
                  zIndex: 5
                }} />
              )}
              {renderAvailableDates()}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => changeDatePage('next')}
              disabled={(currentDatePage + 1) * DATES_PER_PAGE >= allAvailableDates.length}
              style={{
                position: 'absolute',
                right: isMobile ? '-10px' : '4px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '50%',
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                cursor: (currentDatePage + 1) * DATES_PER_PAGE >= allAvailableDates.length ? 'not-allowed' : 'pointer',
                opacity: (currentDatePage + 1) * DATES_PER_PAGE >= allAvailableDates.length ? 0.5 : 1
              }}
            >
              <i className="bx bx-chevron-right" style={{ color: '#666', fontSize: isMobile ? '16px' : '18px' }}></i>
            </button>
          </div>
        </div>

        {/* Time Slots Section */}
        <div style={{ padding: '16px 20px' }}>
          {renderAvailableTimes()}
        </div>

        {/* Contact Section */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #f0f0f0', backgroundColor: '#f9f9f9' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ backgroundColor: '#FFF7EE', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <i className="bx bx-phone" style={{ color: '#FF8F2D', fontSize: '18px' }}></i>
            </div>
            <div>
              <span style={{ fontSize: '13px', color: '#666', display: 'block' }}>Not finding a time that works for you?</span>
              <span style={{ fontSize: '13px', color: '#666' }}>Contact us at <a href="tel:980-890-7995" style={{ color: '#FF8F2D', fontWeight: 500, textDecoration: 'none' }}>980-890-7995</a> and schedule your appointment by phone!</span>
            </div>
          </div>
        </div>

        {/* Confirm Appointment Modal */}
        <ConfirmAppointmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={() => {
            setSelectedDate(null);
            setSelectedTime(null);
            setIsModalOpen(false);
          }}
          therapistName={`${therapistFirstName} ${therapistLastName}`}
          therapistId={therapistId}
          clientId={clientId}
          date={selectedDate ? moment(new Date(currentYear, currentMonth, selectedDate)).format("dddd, MMMM D, YYYY") : ""}
          time={selectedTime || ""}
          selectedDateTime={selectedDate && selectedTime ? (() => {
            const dateTime = moment(new Date(currentYear, currentMonth, selectedDate));
            const timeMatch = selectedTime.match(/(\d+):(\d+)\s(AM|PM)/);
            if (timeMatch) {
              const [hour, minute, period] = timeMatch.slice(1);
              let hourNumber = parseInt(hour);
              if (period === "PM" && hourNumber < 12) hourNumber += 12;
              else if (period === "AM" && hourNumber === 12) hourNumber = 0;
              dateTime.set({ hour: hourNumber, minute: parseInt(minute), second: 0, millisecond: 0 });
            }
            return dateTime.toDate();
          })() : new Date()}
        />
      </CardBody>
    </Card>
  );
};

export default ClientDashboardCalendar;